import * as React from 'react';
import CSSTransition from 'react-transition-group/CSSTransition';
import * as cookie from 'react-cookie';

import { css, StyleSheet } from 'aphrodite';
import { accessibilityUtils, colors, combineStyles, fonts, mixins } from '@blend/consumer-components';
import { ProvideTimers, TimerProps } from '@blend/shared-react';
import { diagnostics } from '@blend/ui-common';

import { getOrganizationColors } from 'login-utils/deploymentConfig';
import * as globalWrappers from 'login-utils/globalWrappers';
import * as oneTapAPI from 'login-apis/oneTapAPI';
import AppDocumentTitle from '../Common/AppDocumentTitle/AppDocumentTitle';
import LoadingSpinner from '../Common/LoadingSpinner/LoadingSpinner';
import PaneLayoutConstants from '../../constants/paneLayoutConstants';

import { Tiempos } from '../Common/PaneLayout/paneStyles';
import { loadingStates } from './LoadingStates';

const actionColor = getOrganizationColors().action;
const animationDurationSec = 0.25;
const animationDurationMs = animationDurationSec * 1000;
// each lottie animation is 10 seconds long; we transition the text and the animation in tandem, so the total
// length of time each update text should be on the screen should be
// (animationDurationMs (entrance) + UPDATE_TEXT_INTERVAL_MS + animationDurationMs (exit)) = 10 seconds
const UPDATE_TEXT_INTERVAL_MS = 10000 - animationDurationMs * 2;
const POLL_VALIDATION_COMPLETION_INTERVAL_MS = 1000;

const styles = StyleSheet.create({
  wrapper: {
    position: 'fixed',
    top: 0,
    bottom: 0,
    right: 0,
    left: 0,
    backgroundColor: colors.white,
  },
  contentWrapper: combineStyles(PaneLayoutConstants.splitContentStaticWidthMixin, mixins.margin(0, 'auto'), {
    ...mixins.padding(0, 24),
    textAlign: 'center',
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
  }),
  loading: {
    ...fonts.Roboto,
  },
  loadingTitle: {
    ...mixins.padding(0, 0, 16, 0),
    color: actionColor,
  },
  loadingSubTitle: {
    ...mixins.padding(0, 0, 32, 0),
    fontSize: 18,
    lineHeight: '18px',
  },
  lottieWrapper: {
    width: '70%',
  },
  lottieTitle: combineStyles(Tiempos, {
    ...mixins.margin(0, 0, 24, 0),
    fontSize: 24,
    lineHeight: '28px',
  }),
  lottieSubtitle: {
    ...fonts.Roboto,
    ...mixins.margin(24, 0, 0, 0),
    fontSize: 16,
    lineHeight: '150%',
  },
  spinner: {
    width: '200%',
  },
});

const animationStyles = StyleSheet.create({
  default: {
    opacity: 1,
    transform: `translateY(0px)`,
  },
  enter: combineStyles(mixins.transition('all', animationDurationSec, 'ease-in'), {
    transform: `translateY(0px)`,
  }),
  exit: combineStyles(mixins.transition('all', animationDurationSec, 'ease-out'), {
    opacity: 0,
    transform: `translateY(-5px)`,
  }),
});

interface LottieLoadingState {
  lottieLoaded: boolean;
  loadingStateIndex: number;
  isAnimating: boolean;
}

// TODO (ONE-457): Make this a more reusable component so it is not as tightly tied to one-tap mortgage copy
export class LottieLoading extends React.PureComponent<TimerProps, LottieLoadingState> {
  state = {
    lottieLoaded: false,
    loadingStateIndex: 0,
    isAnimating: false,
  };

  Lottie;
  animations;

  // TODO (ONE-63): Redirect to Buying Power or Getting Started after loading state finishes

  /* istanbul ignore next */
  async importLottie() {
    const reactLottie = await import('react-lottie');
    return reactLottie.default;
  }

  /* istanbul ignore next */
  async importAnimations() {
    return Promise.all([
      import('./10s_ModularHouse.json'),
      import('./10s_DataMotion.json'),
      import('./10s_Avatar.json'),
    ]);
  }

  onAnimationEntered = () => {
    const { reactSafeSetTimeout } = this.props;
    reactSafeSetTimeout(() => {
      this.setState({ isAnimating: true });
      accessibilityUtils.announce(`Content changed.`);
    }, UPDATE_TEXT_INTERVAL_MS);
  };

  onAnimationExited = () => {
    const { loadingStateIndex } = this.state;
    // After looping through all loading states for the first round. Keep looping between the last two loading state
    // until ingestion is finished.
    const nextIndex = loadingStateIndex === 3 ? 2 : loadingStateIndex + 1;
    accessibilityUtils.announce(`Loading.`);
    this.setState({ loadingStateIndex: nextIndex, isAnimating: false });
  };

  importAnimationsForLottieLoading = async () => {
    try {
      const [Lottie, animations] = await Promise.all([this.importLottie(), this.importAnimations()]);
      this.Lottie = Lottie;
      this.animations = animations;
    } catch (e) {
      // TODO (ONE-63): set error state and render error message when we have the check for ingestion finish
      /* istanbul ignore next */
      throw e;
    }
  };

  /* istanbul ignore next */
  pollValidationStatus = async () => {
    const { reactSafeSetTimeout } = this.props;
    try {
      diagnostics.info(`Checking job status...`);
      const loanId = cookie.load('selectedProductId');
      const status = await oneTapAPI.getOneTapValidationStatus(loanId);
      diagnostics.info(`Job status is: ${status}`);
      // if it finishes with either success or failure, we reload and let the router take us to borrower
      /* istanbul ignore next */
      if (status === 'COMPLETED' || status === 'FAILED') {
        // Necessary to prevent a double refresh where we get re-routed to this component after reloading the window
        diagnostics.info(`Job status is: ${status} - refreshing`);
        (cookie as any).remove('oneTapValidating');
        globalWrappers.getLocation().reload();
        return;
      }
    } catch (e) {
      diagnostics.error(e);
    } finally {
      // want to make sure this runs even if the request fails for whatever reason
      reactSafeSetTimeout(this.pollValidationStatus, POLL_VALIDATION_COMPLETION_INTERVAL_MS);
    }
  };

  async componentDidMount() {
    this.pollValidationStatus();
    await this.importAnimationsForLottieLoading();
    this.setState({ lottieLoaded: true }, () => {
      this.onAnimationEntered();
    });
  }

  render() {
    const { lottieLoaded, loadingStateIndex, isAnimating } = this.state;
    const title = 'Loading';
    return (
      <AppDocumentTitle title={title}>
        <div className={css(styles.wrapper)}>
          <div className={css(styles.contentWrapper)}>
            {lottieLoaded ? (
              <div>
                <div data-it="LoadingState-topText" className={css(styles.lottieTitle)}>
                  {loadingStates[loadingStateIndex].topText}
                </div>
                <div>
                  {/* sets role img to override Lottie's default button role */}
                  <div role="img" aria-label={loadingStates[loadingStateIndex].altText}>
                    <this.Lottie
                      options={{
                        animationData:
                          this.animations[loadingStateIndex] || this.animations[this.animations.length - 1],
                      }}
                      width="70%"
                      // IE seems to need both height and width specified so that the SVG is sized properly. Using 40vh
                      // so that the svg is responsive to both width and height, but it's a somewhat arbitrary number
                      // https://stackoverflow.com/questions/30415980/svgs-not-scaling-properly-in-ie-has-extra-space
                      height="40vh"
                      data-it="LoadingState-animation"
                    />
                  </div>
                </div>
                <CSSTransition
                  in={!isAnimating}
                  timeout={animationDurationMs}
                  classNames={{
                    enter: css(animationStyles.default, animationStyles.enter),
                    enterActive: 'transition-active',
                    exit: css(animationStyles.default, animationStyles.exit),
                    exitActive: 'transition-active',
                  }}
                  onEntered={this.onAnimationEntered}
                  onExited={this.onAnimationExited}
                >
                  <div className={css(styles.lottieSubtitle)} data-it="LoadingState-bottomText">
                    {loadingStates[loadingStateIndex].bottomText}
                  </div>
                </CSSTransition>
              </div>
            ) : (
              <div className={css(styles.loading)} data-it="it-loading-lottie">
                <div className={css(styles.loadingTitle)}>Your dream home awaits</div>
                <div className={css(styles.loadingSubTitle)}>
                  We're reviewing your info to see what you may qualify for
                </div>
                <LoadingSpinner />
              </div>
            )}
          </div>
        </div>
      </AppDocumentTitle>
    );
  }
}
/* istanbul ignore next */
export default () => <ProvideTimers render={timerProps => <LottieLoading {...timerProps} />} />;
